import {
  avatar,
  background,
  captions,
  glyph,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `BigTitleLayout` (`layouts/BigTitleLayout.tsx`): the subtitle, a huge
 * title, bars, captions, then a quotes glyph, the speaker's avatar and their
 * handle at the foot. The title box holds three lines (portrait), two
 * (square) or one (landscape), as v1 clamped it. Intended differences: the
 * quotes are a text glyph (`”`) sized so its ink fills the SVG's 88×74 box
 * (the glyph's ink is about a third of its em, so the font is three times
 * the box and overflows it, which the text layer allows), not the SVG; the
 * handle is centred on the avatar row at a 1.2 line height; the avatar sits
 * between the glyph and the handle, where v1 put it at the right edge — the
 * watermark lives there now; in landscape the bars span the title's width
 * (v1 capped them at 1000 px, which read as bars that failed to stretch).
 * Intended difference (#547): in portrait the foot row (glyph, avatar,
 * handle) sits 32 px higher, so a long handle stays clear of the watermark.
 */
const bars = { variant: 'bold' as const }

export const bigTitleBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      text('portrait', 'subtitle', [60, 60, 760, 50], {
        size: 40,
        weight: 500,
        lineHeight: 1.26,
      }),
      text('portrait', 'title', [60, 195, 960, 390], {
        size: 100,
        weight: 600,
        lineHeight: 1.3,
      }),
      visualizer('portrait', [60, 643, 960, 160], bars),
      captions('portrait', [60, 860, 960, 711], 'end'),
      glyph('portrait', 'quote', '”', [60, 1703, 88, 74], 224, 'border'),
      avatar('portrait', 199, 1698, 130),
      {
        ...speaker('portrait', [359, 1736, 661, 55], 'handle', {
          size: 46,
          weight: 700,
          lineHeight: 1.2,
        }),
        id: 'speaker',
      },
    ],
    square: [
      background(),
      text('square', 'subtitle', [60, 60, 760, 50], {
        size: 40,
        weight: 500,
        lineHeight: 1.26,
      }),
      text('square', 'title', [60, 154, 960, 213], {
        size: 82,
        weight: 600,
        lineHeight: 1.3,
      }),
      visualizer('square', [60, 402, 960, 160], bars),
      captions('square', [60, 598, 960, 232], 'end'),
      glyph('square', 'quote', '”', [60, 919, 74, 62], 188, 'border'),
      avatar('square', 174, 920, 100),
      {
        ...speaker('square', [304, 947, 716, 46], 'handle', {
          size: 38,
          weight: 700,
          lineHeight: 1.2,
        }),
        id: 'speaker',
      },
    ],
    landscape: [
      background(),
      text('landscape', 'subtitle', [80, 80, 1760, 50], {
        size: 40,
        weight: 500,
        lineHeight: 1.26,
      }),
      text('landscape', 'title', [80, 195, 1760, 117], {
        size: 90,
        weight: 600,
        lineHeight: 1.3,
      }),
      visualizer('landscape', [80, 362, 1760, 160], bars),
      captions('landscape', [80, 573, 1760, 265], 'end'),
      glyph('landscape', 'quote', '”', [80, 890, 88, 74], 224, 'border'),
      avatar('landscape', 219, 900, 100),
      {
        ...speaker('landscape', [349, 923, 1491, 55], 'handle', {
          size: 46,
          weight: 700,
          lineHeight: 1.2,
        }),
        id: 'speaker',
      },
    ],
  },
}
